import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { doc as fsDoc, getDoc, increment } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { experimentStamp } from './experiments';
import type {
  Capture,
  CaptureMode,
  ConfirmedData,
  ExtractionResult,
  Instrument,
} from '../lib/types';

export const capturesCol = (projectId: string) =>
  collection(db, 'projects', projectId, 'captures');

export const captureDoc = (projectId: string, id: string) =>
  doc(db, 'projects', projectId, 'captures', id);

export function newCaptureId(projectId: string) {
  return doc(capturesCol(projectId)).id;
}

export function watchCaptures(projectId: string, cb: (c: Capture[]) => void, max = 100) {
  const q = query(capturesCol(projectId), orderBy('createdAt', 'desc'), limit(max));
  return onSnapshot(q, (snap) =>
    cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Capture)),
  );
}

/**
 * Every number in the workbook traces to a capture — including hand-typed ones,
 * which land here with mode "manual" and no photo or transcript. Uniform
 * provenance is the whole point of keeping this collection.
 */
export async function createCapture(
  projectId: string,
  captureId: string,
  fields: {
    mode: CaptureMode;
    instrument: Instrument;
    capturedByUid: string;
    capturedByName?: string | null;
    storagePath?: string | null;
    thumbnailPath?: string | null;
    transcript?: string | null;
    status?: Capture['status'];
    /** Snapshot of the reader this was read against, so its fields travel with it. */
    reader?: { id: string; name: string; fields: { key: string; label: string; unit: string; decimals: number }[] };
  },
) {
  await setDoc(captureDoc(projectId, captureId), {
    mode: fields.mode,
    capturedByUid: fields.capturedByUid,
    capturedByName: fields.capturedByName ?? null,
    downloadUrl: null,
    instrument: fields.instrument,
    storagePath: fields.storagePath ?? null,
    thumbnailPath: fields.thumbnailPath ?? null,
    transcript: fields.transcript ?? null,
    status: fields.status ?? 'pending',
    ...(fields.reader ? { reader: fields.reader } : {}),
    ...experimentStamp(projectId),
    rawExtraction: null,
    confirmedData: null,
    appliedTo: [],
    errorMessage: null,
    createdAt: serverTimestamp(),
    confirmedAt: null,
  });
}

export async function setExtraction(
  projectId: string,
  captureId: string,
  result: ExtractionResult,
) {
  await updateDoc(captureDoc(projectId, captureId), {
    rawExtraction: result,
    status: 'extracted',
    errorMessage: null,
  });
}

export async function setCaptureFailed(
  projectId: string,
  captureId: string,
  message: string,
  rawText?: string,
) {
  await updateDoc(captureDoc(projectId, captureId), {
    status: 'failed',
    errorMessage: message,
    ...(rawText ? { rawExtraction: { rawText } } : {}),
  });
}

export async function confirmCapture(
  projectId: string,
  captureId: string,
  confirmedData: ConfirmedData,
  appliedTo: string[],
  columnsSeen?: { heading: string; mappedTo: string | null }[],
) {
  await updateDoc(captureDoc(projectId, captureId), {
    confirmedData,
    appliedTo,
    status: 'confirmed',
    confirmedAt: serverTimestamp(),
  });

  // A human accepting the result is the strongest evidence the headings were
  // read correctly, so promote those mappings above mere observations. Failing
  // here must never cost the user their confirmed data.
  if (columnsSeen?.length) {
    try {
      const ref = fsDoc(db, 'projects', projectId, 'knowledge', 'headers');
      const existing = (await getDoc(ref)).data()?.entries ?? {};
      const patch: Record<string, unknown> = {};
      for (const col of columnsSeen) {
        const heading = (col?.heading ?? '').trim();
        if (!heading || !col?.mappedTo) continue;
        const prev = (existing as Record<string, { field?: string }>)[heading];
        patch[`entries.${heading}.field`] = col.mappedTo;
        patch[`entries.${heading}.confirmed`] =
          prev?.field === col.mappedTo ? increment(1) : 1;
      }
      if (Object.keys(patch).length) await setDoc(ref, patch, { merge: true });
    } catch {
      // The glossary is an optimisation; never let it block a confirmation.
    }
  }
}

/** Rejecting keeps the record — the photo and the raw output stay for the audit trail. */
export async function rejectCapture(projectId: string, captureId: string) {
  await updateDoc(captureDoc(projectId, captureId), {
    status: 'rejected',
    confirmedAt: serverTimestamp(),
  });
}
